/**
 * Static drill recommendations per posture orientation pattern.
 * Keys must match normalized `classification.pattern` values from classifyPosture()
 * (e.g. "Swayback", "Anterior Pelvic Tilt" — see evaluation-rules.yaml display.patterns).
 *
 * Video URLs align with Phase 1 rule docs and the drill library:
 * - RuleDoc-3.2-SB-Phase-1 (swayback)
 * - RuleDoc-3.1-APT-Phase-1 (anterior pelvic tilt)
 */

export interface DrillEntry {
  name: string;
  description: string;
  /** Full YouTube watch URL from the drill library (when available) */
  videoUrl?: string;
}

export type DrillMap = Record<string, DrillEntry[]>;

export const DRILLS_BY_PATTERN: DrillMap = {
  swayback: [
    {
      name: 'Supine Chest Opener',
      description:
        'Opens up the front of chest & rib cage to be able to expand better.',
      videoUrl: 'https://www.youtube.com/watch?v=y2rp2sQu1-8&list=PLFq2zv7d_8BAAJ1QtacapB_n-GmfQRThq',
    },
    {
      name: 'Prone Press Up',
      description:
        'Thoracic extension (without overdoing it) and upper front rib cage expansion.',
      videoUrl: 'https://www.youtube.com/watch?v=NKmyca8s5w8&list=PLFq2zv7d_8BAAJ1QtacapB_n-GmfQRThq',
    },
  ],

  anteriorPelvicTilt: [
    {
      name: '90/90 Hip Lift with Ball Hold',
      description:
        'Achieves posterior pelvic tilt and hamstring activation to pull pelvis back',
      videoUrl: 'https://www.youtube.com/watch?v=TyhUuANFYrM&list=PLFq2zv7d_8BC-6xngCjSjSd1fbRub6nZ7',
    },
    {
      name: 'Wall Supported Stack',
      description:
        'Teaches the body to maintain the better pelvic alignment relative to the rest of the body in a standing position',
      videoUrl: 'https://www.youtube.com/watch?v=RwQOYrYl_No',
    },
  ],

  anteriorPelvicTiltPlc: [
    {
      name: 'Supine Chest Opener',
      description:
        'Opens up the front of chest & rib cage to be able to expand better.',
      videoUrl: 'https://www.youtube.com/watch?v=y2rp2sQu1-8&list=PLFq2zv7d_8BAAJ1QtacapB_n-GmfQRThq',
    },
    {
      name: 'Wall Supported Stack',
      description:
        'Teaches the body to maintain the better pelvic alignment relative to the rest of the body in a standing position',
      videoUrl: 'https://www.youtube.com/watch?v=RwQOYrYl_No',
    },
  ],

  neutral: [
    {
      name: "Farmer's Carry",
      description:
        'Hold a weight in each hand (or a bag in each hand). Walk slowly for 20–30 metres, keeping your shoulders back and core lightly braced. Reinforces your neutral posture under load.',
      videoUrl: 'https://www.youtube.com/watch?v=Fkzk_RqlYig',
    },
    {
      name: 'Single-Leg Stance',
      description:
        'Stand on one foot for 30 seconds per side. Keep your hips level (resist the urge to hike the free hip). Challenges the lateral stability that keeps you well-aligned.',
      videoUrl: 'https://www.youtube.com/watch?v=38vK-bSMC1E',
    },
    {
      name: 'Thoracic Extension (Foam Roller)',
      description:
        'Place a foam roller or rolled-up towel across your mid-back. Support your head and gently extend over the roller for 30–60 seconds. Keeps the upper back mobile and open.',
      videoUrl: 'https://www.youtube.com/watch?v=tT8mD2FmtjU',
    },
  ],
};

/** Fallback drills when no pattern-specific set is available. */
export const FALLBACK_DRILLS: DrillEntry[] = DRILLS_BY_PATTERN.neutral;

export function getDrillsForPattern(pattern: string | undefined): DrillEntry[] {
  if (!pattern) return [];
  const key = pattern.toLowerCase().replace(/[\s-_()]/g, '');

  if (
    key.includes('posteriorlowercompression') ||
    key.includes('plc') ||
    (key.includes('apt') && key.includes('sway'))
  ) {
    return DRILLS_BY_PATTERN.anteriorPelvicTiltPlc;
  }
  if (key.includes('sway') || key === 'sb') {
    return DRILLS_BY_PATTERN.swayback;
  }
  if (key.includes('anterior') || key.includes('apt')) {
    return DRILLS_BY_PATTERN.anteriorPelvicTilt;
  }
  if (key === 'n' || key.includes('neutral') || key.includes('stack')) {
    return DRILLS_BY_PATTERN.neutral;
  }

  return FALLBACK_DRILLS;
}
